import React, { FC, SVGProps, ReactElement } from 'react';
import classnames from 'classnames';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons/faCircleNotch';

import { Maybe } from '@interfaces/graphql-typings';

import styles from './buttonStyles.scss';

export enum BUTTON_TYPES {
  SECONDARY = 'secondary',
  PRIMARY = 'primary',
  MAJOR = 'major',
  DANGER = 'danger',
  LINK = 'link',
  BUTTON = 'button',
  SUBMIT = 'submit'
}

export enum BUTTON_SIZES {
  MICRO = 'micro',
  TINIEST = 'tiniest',
  TINY = 'tiny',
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large'
}

export enum BUTTON_WIDTHS {
  REGULAR = 'regWidth',
  FULL = 'fullWidth',
  WIDE = 'wide'
}

const Button: FC<{
  id?: string;
  analyticsTag?: string;
  buttonType?: BUTTON_TYPES.SUBMIT | BUTTON_TYPES.BUTTON;
  children?: ReactElement | ((props: any) => ReactElement);
  className?: string;
  disabled?: boolean;
  faIcon?: Maybe<IconProp>;
  icon?: FC<SVGProps<SVGElement>>;
  iconStyle?: string;
  inherit?: boolean;
  isLoading?: boolean;
  joyrideTarget?: string;
  onClick?: (...props: any[]) => void;
  size?: BUTTON_SIZES;
  target?: string;
  text?: Maybe<string> | ReactElement;
  title?: string;
  type?: BUTTON_TYPES;
  url?: string;
  value?: string;
  width?: BUTTON_WIDTHS;
}> = ({
  id,
  analyticsTag,
  buttonType = BUTTON_TYPES.SUBMIT,
  className,
  disabled,
  faIcon,
  icon,
  iconStyle,
  inherit,
  isLoading,
  joyrideTarget,
  onClick,
  size = BUTTON_SIZES.MEDIUM,
  target,
  text = '',
  title,
  type = BUTTON_TYPES.SECONDARY,
  url,
  value,
  width = BUTTON_WIDTHS.REGULAR
}) => {
  let iconEl;

  if (faIcon) {
    iconEl = <FontAwesomeIcon icon={faIcon} />;
  }

  if (icon) {
    const Icon = icon;
    iconEl = <Icon />;
  }

  if (isLoading) {
    iconEl = <FontAwesomeIcon icon={faCircleNotch} spin />;
  }

  const buttonClassNames = classnames(
    [styles[size]],
    [styles[width]],
    {
      [styles.button]: type === BUTTON_TYPES.SECONDARY,
      [styles.buttonPrimary]: type === BUTTON_TYPES.PRIMARY,
      [styles.buttonMajor]: type === BUTTON_TYPES.MAJOR,
      [styles.buttonDanger]: type === BUTTON_TYPES.DANGER,
      [styles.buttonLink]: type === BUTTON_TYPES.LINK,
      [styles.inherit]: inherit,
      [styles['is-disabled']]: disabled || isLoading
    },
    className
  );
  const iconClassNames = classnames(
    {
      [styles.buttonIcon]: true,
      [styles.primary]: ['primary', 'major', 'danger'].indexOf(type) !== -1
    },
    [styles[size]],
    iconStyle
  );

  if (url) {
    return (
      <a
        id={id}
        data-testid={id}
        href={url}
        onClick={onClick}
        className={buttonClassNames}
        data-heap={analyticsTag}
        data-joyride-target={joyrideTarget}
        target={target}
      >
        <div className={iconClassNames}>{iconEl}</div>
        <span>{text}</span>
      </a>
    );
  }

  return (
    <button
      id={id}
      data-testid={id}
      onClick={onClick}
      disabled={disabled || isLoading}
      className={buttonClassNames}
      value={value}
      title={title}
      type={buttonType}
      data-heap={analyticsTag}
      data-joyride-target={joyrideTarget}
    >
      <div className={iconClassNames}>{iconEl}</div>
      <span>{text}</span>
    </button>
  );
};

export default Button;
